/**
 * AddToFavorite — the heart toggle used wherever a product can be saved.
 *
 * App component, not vendored: upstream repeats this markup per screen. Grey heart on a
 * white circle until saved, red on hover, filled red on a glass circle once saved, with
 * the heart tilting on hover. Saving plays a short pop (`dnd-fav-pop` in `keyframes.js`);
 * it is started by the click, not by `saved` turning true, so items that are already
 * saved don't pop when they render.
 *
 * Presentational only — the caller owns the favourites state and passes `saved` and
 * `onToggle(next)`. The click doesn't propagate, so it can sit inside a clickable card.
 *
 * Position it from outside with `className` (e.g. `absolute top-2 right-2`).
 */
import React from "react";

import { cx } from "./cx";
import { Icon } from "./Icon.jsx";

const SIZES = {
  sm: { box: "h-[32px] w-[32px]", icon: 20 },
  md: { box: "h-[38px] w-[38px]", icon: 18 },
  lg: { box: "h-[44px] w-[44px]", icon: 22 },
};

export function AddToFavorite({
  saved = false,
  onToggle,
  size = "sm",
  className = "",
  ...rest
}) {
  const [popping, setPopping] = React.useState(false);
  const sz = SIZES[size] || SIZES.sm;

  return (
    <button
      type="button"
      aria-pressed={saved}
      aria-label={saved ? "Remove from saved" : "Save for later"}
      onClick={(event) => {
        event.stopPropagation();
        setPopping(!saved);
        onToggle?.(!saved);
      }}
      onAnimationEnd={(event) => {
        if (event.animationName === "dndFavPop") setPopping(false);
      }}
      className={cx(
        "dnd-fav group/fav inline-flex shrink-0 cursor-pointer items-center justify-center rounded-full border-0 backdrop-blur-[6px]",
        "[transition:background_var(--duration-fast),transform_var(--duration-base)_var(--ease-spring)] active:scale-[0.9] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]",
        sz.box,
        popping && "dnd-fav-pop",
        saved
          ? "bg-[var(--surface-glass)] text-error-500"
          : "bg-card text-ink-3 hover:bg-[var(--action-neutral-hover)] hover:text-error-500",
        className
      )}
      {...rest}
    >
      <Icon
        name="favorite"
        size={sz.icon}
        weight={400}
        fill={saved}
        className={cx(
          "group-hover/fav:rotate-[-8deg] group-hover/fav:scale-[1.1]",
          saved && "scale-[1.1]"
        )}
      />
    </button>
  );
}
